import { SelectChangeEvent } from '@mui/material';
import { getIdFormSelectBranchData } from '../../../../utils/getIdFormSelectBranchData';
import { useRef, useState } from 'react';
import { MerChantModel } from '../../../../models/merChantModel';
import moment from 'moment';
import { CouponEnum } from '../../../../enums/couponEnum';
import { createCouponService } from '../../../../services/coupon';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';

interface Props {
  branchData: MerChantModel[];
  couponType: CouponEnum;
  onClose: VoidFunction;
  onFetchData: Function;
}

const useDeliveryFeeAmountController = ({
  branchData,
  couponType,
  onClose,
  onFetchData,
}: Props) => {
  const [selectBranchDataValue, setSelectBranchDataValue] = useState<string[]>([]);
  const [openConfirmForm, setOpenConfirmForm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loadingButton, setLoadingButton] = useState<boolean>(false);

  const couponName = useRef<HTMLInputElement>(null!);
  const couponCode = useRef<HTMLInputElement>(null!);
  const limitPerUser = useRef<HTMLInputElement>(null!);
  const limitUse = useRef<HTMLInputElement>(null!);
  const buyByPrice = useRef<HTMLInputElement>(null!);
  const startDate = useRef<HTMLInputElement>(null!);
  const endDate = useRef<HTMLInputElement>(null!);
  const discountPrice = useRef<HTMLInputElement>(null!);

  const roleAdmin = useSelector((state: RootState) => state?.auth?.data?.role?.role_type);
  const getBranchId: string[] = getIdFormSelectBranchData(selectBranchDataValue, branchData);
  
  const handleSelectedBranch = (event: SelectChangeEvent<string>) => {
    const {
      target: { value },
    } = event;
    setSelectBranchDataValue(typeof value === 'string' ? value.split(',') : value);
  };

  const handleValidate = (): boolean => {
    // Validation rules
    if (getBranchId.length === 0) {
      setErrorMessage('At least one merchant ID is required.');
      return false;
    }

    if (!couponCode.current?.value || couponCode.current.value.trim() === '') {
      setErrorMessage('Coupon code is required.');
      return false;
    }

    if (!couponType || couponType.trim() === '') {
      setErrorMessage('Coupon type is required.');
      return false;
    }

    if (
      buyByPrice.current?.value &&
      (isNaN(Number(buyByPrice.current.value)) || Number(buyByPrice.current.value) < 0)
    ) {
      setErrorMessage('Buy by amount must be a non-negative number.');
      return false;
    }
    if (
      !discountPrice.current?.value ||
      isNaN(Number(discountPrice.current.value)) ||
      Number(discountPrice.current.value) <= 0
    ) {
      setErrorMessage('Discount must be a positive number.');
      return false;
    }

    if (!couponName.current?.value || couponName.current.value.trim() === '') {
      setErrorMessage('Coupon name is required.');
      return false;
    }
    const start = moment(startDate.current.value, 'MM/DD/YYYY');
    const end = moment(endDate.current.value, 'MM/DD/YYYY');
    if (start.isSameOrAfter(end)) {
      setErrorMessage('Start date must be before the end date.');
      return false;
    }
    // Clear any previous error messages
    setErrorMessage('');
    return true;
  };

  const handleSubmitForm = (): void => {
    if (handleValidate()) {
      setOpenConfirmForm(true);
      // setErrorMessage('')
    }
  };

  const handleCloseConfirmFom = () => {
    setOpenConfirmForm(false);
  };

  const formCoupon = () => ({
    merchantIds: getBranchId,
    couponCode: couponCode.current.value,
    couponType: couponType,
    discount: discountPrice.current.value,
    buyByAmount: buyByPrice.current.value,
    name: couponName.current.value,
    startDate: moment(new Date(startDate.current.value)).format('MM/DD/YYYY'),
    endDate: moment(new Date(endDate.current.value)).format('MM/DD/YYYY'),
    limitUsePerCustomer: limitPerUser.current.value,
    limitUsing: limitUse.current.value,
  });

  const handleCreatePromotion = async () => {
    try {
      const formData = formCoupon();
      console.log(formData);

      const res = await createCouponService(formData);
      console.log(res);

      await onFetchData();
      onClose();
    } catch (error: any) {
      //   console.log(error);
      setOpenConfirmForm(false);
      try {
        const getErrorMessage: string = error.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        // setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingButton(false);
    }
  };

  return {
    handleCreatePromotion,
    handleCloseConfirmFom,
    handleSelectedBranch,
    handleSubmitForm,
    selectBranchDataValue,
    openConfirmForm,
    errorMessage,
    loadingButton,
    couponName,
    couponCode,
    buyByPrice,
    startDate,
    endDate,
    limitPerUser,
    discountPrice,
    limitUse,
    roleAdmin
  };
};

export default useDeliveryFeeAmountController;
